import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { FloatingInput, Button } from '@piplapps/pipl-ui';
import TranslationUtils, { getText } from '../../../Utils/TranslationUtils';
import styles from './LeadVettingFormStyles.module.less';
import ApiService from '../../../Services/ApiService';
import { trackEvent } from '../utils';
import { validateBasicEmail, validatePhone } from '../../../Utils/GenericUtils';
import AnalyticsEventNames from '../../../Utils/AnalyticsHandler/AnalyticsEventNames';
import AnalyticsHandler from '../../../Utils/AnalyticsHandler/AnalyticsHandler';
import PageviewHandler from '../../../Utils/AnalyticsHandler/PageviewHandler';

const VettingStep1 = (props) => {
  const [loadingEmail, setLoading] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const validate = (values) => {
    const errors = {};
    for (const value in values) {
      if (values[value] === '' || !values[value] || values[value].trim() === '') {
        errors[value] = getText('ERROR_REQUIRED_FIELD');
      }
    }

    if (!validateBasicEmail(values.email?.trim())) {
      errors.email = getText('ERROR_INVALID_EMAIL');
    }
    if (emailError) {
      errors.email = getText(emailError);
    }
    if (!validatePhone(values.phone)) {
      errors.phone = getText('ERROR_INVALID_PHONE');
    }
    if (Object.values(errors).length > 0) {
      props.removeCompleted(1);
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      firstName: props.fields?.firstName ? props.fields.firstName : '',
      lastName: props.fields?.lastName ? props.fields.lastName : '',
      email: props.fields?.email ? props.fields.email : '',
      phone: props.fields?.phone ? props.fields.phone : '',
    },
    validate,
    onSubmit: (values) => {
      trackEvent('Step1', '');
      props.completeStep(1);
      props.onSubmit(values);
    },
  });
  useEffect(() => {
    formik.setFieldValue('email', props.fields?.email);
  }, [props.fields]);
  async function checkEmail(email) {
    if (validateBasicEmail(email)) {
      setEmailError('evaluating email..');
      setLoading(true);
      try {
        await ApiService.vettingCheckEmail({ email });
        setLoading(false);
        setEmailError(null);
        formik.setFieldError('email', null);
      } catch (e) {
        setLoading(false);
        setEmailError(e.response.data[0]);
        formik.setFieldError('email', getText(e.response.data[0]));
      }
    }
    return true;
  }
  const onBlur = (e, fieldName) => {
    const isPIIField = ['email', 'firstName', 'lastName', 'phone'].includes(fieldName);
    const fieldObj = { [fieldName]: formik.values[fieldName] };
    trackEvent(fieldName, isPIIField ? '' : e.target.value);
    formik.setFieldTouched(fieldName, true);
    props.updateFields(fieldObj);
  };

  return (
    <>
      <PageviewHandler path="step1/" />
      <div className={styles.form}>
        <div className={styles.headerWrapper}>
          <div className={styles.pageTitle}>
            <span>{gettext('Contact Information')}</span>
          </div>
        </div>
        <div>
          <form onSubmit={formik.handleSubmit} id="vetting-step-1-form">
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_firstName"
                name="firstName"
                placeholder={gettext('First Name')}
                testId="firstName-input"
                onChange={formik.handleChange}
                onBlur={(e) => onBlur(e, 'firstName')}
                value={formik.values.firstName ? formik.values.firstName : ''}
                forcedError={formik.errors.firstName && formik.touched.firstName ? formik.errors.firstName : null}
                error={formik.errors.firstName}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_lastName"
                name="lastName"
                placeholder={gettext('Last Name')}
                testId="lastName-input"
                onChange={formik.handleChange}
                onBlur={(e) => onBlur(e, 'lastName')}
                value={formik.values.lastName ? formik.values.lastName : ''}
                forcedError={formik.errors.lastName && formik.touched.lastName ? formik.errors.lastName : null}
                error={formik.errors.lastName}
              />
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_email"
                name="email"
                placeholder={gettext('Business Email')}
                testId="email-input"
                onChange={formik.handleChange}
                onBlur={async (e) => {
                  onBlur(e, 'email');
                  await checkEmail(formik.values.email?.trim());
                }}
                value={formik.values.email ? formik.values.email : ''}
                forcedError={formik.errors.email && formik.touched.email ? formik.errors.email : null}
                error={formik.errors.email}
                isLoading={loadingEmail}
              />
              <div className={styles.emailText}>{gettext("Personal accounts like Gmail won't be accepted")}</div>
            </div>
            <div className={styles.fieldRow}>
              <FloatingInput
                id="id_phone"
                name="phone"
                placeholder={gettext('Phone Number')}
                testId="phone-input"
                onChange={formik.handleChange}
                onBlur={(e) => onBlur(e, 'phone')}
                value={formik.values.phone ? formik.values.phone : ''}
                forcedError={formik.errors.phone && formik.touched.phone ? formik.errors.phone : null}
                error={formik.errors.phone}
              />
            </div>
            <div className={styles.proceedButton}>
              <AnalyticsHandler eventName={AnalyticsEventNames.SUBMIT_STEP_1}>
                <Button
                  id="submit-button"
                  buttonClass="primary-green"
                  type="submit"
                  text={gettext('Proceed')}
                  isLoading={formik.touched.email && loadingEmail}
                  loadingText={gettext('Validating')}
                />
              </AnalyticsHandler>
            </div>
          </form>
        </div>
        <div style={{ textAlign: 'center' }} className={styles.fieldRow}>
          <div className={styles.alreadyHaveAccountHeader}>
            <div className={styles.titleBar} />
            <span className="highlight">{TranslationUtils.getText('ALREADY_HAS_ACCOUNT_TITLE')}</span>
          </div>
          <a className={styles.signinLink} href={`${ApiService.generateLink('login/')}`}>
            <span>{TranslationUtils.getText('SIGN_IN')}</span>
          </a>
        </div>
      </div>
    </>
  );
};
VettingStep1.propTypes = {
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  fields: PropTypes.object,
  completeStep: PropTypes.func,
  removeCompleted: PropTypes.func,
  updateFields: PropTypes.func,
};

export default VettingStep1;
